/**
 * The notification bell.
 *
 * Its reason for existing is that email is not a workflow. Half the policy's participants read
 * mail on a phone between site visits, and "I never got it" has no answer a mail server can give.
 * This one can: the same event that queued the email wrote a notice here, in the same
 * transaction, so the record is either both or neither.
 *
 * Deliberately not a live socket. A thirty-second poll while the tab is open is one small query
 * against a database already serving the page, and a WebSocket would be a second connection, a
 * reconnect strategy and a proxy configuration to keep the same information no fresher than the
 * jobs that produce it.
 */
import { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from '@/lib/api';
import { formatInstant } from '@/lib/dates';
import { cn } from '@/lib/utils';
import { Button, Spinner } from '@/components/ui';
import type { Notification } from '@/lib/types';

const POLL_MS = 30_000;

export function Bell() {
  const [open, setOpen] = useState(false);
  const panel = useRef<HTMLDivElement>(null);
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  const list = useQuery({
    queryKey: ['notifications'],
    queryFn: () => api.notifications(20),
    refetchInterval: POLL_MS,
    // No background polling on a tab nobody is looking at: it is a bell, not a monitor.
    refetchIntervalInBackground: false,
  });

  const markRead = useMutation({
    mutationFn: (id: number) => api.readNotification(id),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['notifications'] }),
  });
  const markAll = useMutation({
    mutationFn: () => api.readAllNotifications(),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['notifications'] }),
  });

  // Click-away and Escape, because a panel that only closes by clicking the bell again is a
  // panel people leave open over the page they are trying to read.
  useEffect(() => {
    if (!open) return;
    const away = (event: MouseEvent) => {
      if (panel.current && !panel.current.contains(event.target as Node)) setOpen(false);
    };
    const escape = (event: KeyboardEvent) => {
      if (event.key === 'Escape') setOpen(false);
    };
    document.addEventListener('mousedown', away);
    document.addEventListener('keydown', escape);
    return () => {
      document.removeEventListener('mousedown', away);
      document.removeEventListener('keydown', escape);
    };
  }, [open]);

  const unread = list.data?.unread ?? 0;
  const notifications = list.data?.notifications ?? [];

  const go = (notification: Notification) => {
    if (notification.read_at === null) markRead.mutate(notification.id);
    setOpen(false);
    if (notification.link) navigate(notification.link);
  };

  return (
    <div className="relative" ref={panel}>
      <button
        type="button"
        onClick={() => setOpen((was) => !was)}
        aria-expanded={open}
        aria-label={unread > 0 ? `Notifications — ${unread} unread` : 'Notifications'}
        className="hover:bg-accent focus-visible:ring-ring relative inline-flex h-9 w-9 items-center justify-center rounded-md focus-visible:ring-2 focus-visible:outline-none"
      >
        <BellIcon />
        {unread > 0 ? (
          <span className="bg-destructive text-destructive-foreground absolute -top-0.5 -right-0.5 min-w-4 rounded-full px-1 text-[0.65rem] leading-4 font-semibold">
            {unread > 9 ? '9+' : unread}
          </span>
        ) : null}
      </button>

      {open ? (
        <div className="border-border bg-background absolute right-0 z-20 mt-1 w-[min(24rem,calc(100vw-2rem))] overflow-hidden rounded-md border shadow-lg">
          <div className="border-border flex items-center justify-between border-b px-3 py-2">
            <p className="text-sm font-medium">Notifications</p>
            {unread > 0 ? (
              <Button
                size="sm"
                variant="ghost"
                busy={markAll.isPending}
                onClick={() => markAll.mutate()}
              >
                Mark all read
              </Button>
            ) : null}
          </div>

          <div className="max-h-[60vh] overflow-y-auto">
            {list.isPending ? (
              <p className="text-muted-foreground flex items-center gap-2 px-3 py-6 text-sm">
                <Spinner /> Loading
              </p>
            ) : notifications.length === 0 ? (
              <p className="text-muted-foreground px-3 py-6 text-sm">
                Nothing yet. Submissions, endorsements and claim deadlines show up here.
              </p>
            ) : (
              <ul>
                {notifications.map((notification) => (
                  <li key={notification.id}>
                    <button
                      type="button"
                      onClick={() => go(notification)}
                      className={cn(
                        'hover:bg-accent block w-full px-3 py-2 text-left',
                        notification.read_at === null ? 'bg-accent/40' : '',
                      )}
                    >
                      <span className="flex items-start gap-2">
                        {notification.read_at === null ? (
                          <span
                            className="bg-primary mt-1.5 h-1.5 w-1.5 shrink-0 rounded-full"
                            aria-hidden
                          />
                        ) : (
                          <span className="mt-1.5 h-1.5 w-1.5 shrink-0" aria-hidden />
                        )}
                        <span className="min-w-0">
                          <span className="block text-sm font-medium">{notification.title}</span>
                          {notification.body ? (
                            <span className="text-muted-foreground block text-xs">
                              {notification.body}
                            </span>
                          ) : null}
                          <span className="text-muted-foreground block text-[0.7rem]">
                            {formatInstant(notification.created_at)}
                          </span>
                        </span>
                      </span>
                    </button>
                  </li>
                ))}
              </ul>
            )}
          </div>
        </div>
      ) : null}
    </div>
  );
}

/** Inline, because one icon is not worth an icon library in the bundle. */
function BellIcon() {
  return (
    <svg
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.6"
      strokeLinecap="round"
      strokeLinejoin="round"
      className="h-[1.15rem] w-[1.15rem]"
      aria-hidden
    >
      <path d="M18 8A6 6 0 0 0 6 8c0 7-3 9-3 9h18s-3-2-3-9" />
      <path d="M13.7 21a2 2 0 0 1-3.4 0" />
    </svg>
  );
}
